// hooks/useVideos.ts — hook for the Videos page data.
//
// Pure read-only (no store — video data is static seed in prototype phase).
// Components must NOT import videosService directly.

import { useState, useEffect } from 'react';
import { videosService } from '@/core/services/videosService';
import type { VideoItem, VideoCreator } from '@/core/models/video';

export interface VideosHook {
  /** All videos, including shorts, in seed order. */
  videos: VideoItem[];
  /** Web-only Shorts rail items. */
  shorts: VideoItem[];
  /** Top Creators rail. */
  creators: VideoCreator[];
  loaded: boolean;
}

export function useVideos(): VideosHook {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [shorts, setShorts] = useState<VideoItem[]>([]);
  const [creators, setCreators] = useState<VideoCreator[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void Promise.all([videosService.all(), videosService.shorts(), videosService.creators()]).then(
      ([v, s, c]) => {
        setVideos(v);
        setShorts(s);
        setCreators(c);
        setLoaded(true);
      },
    );
  }, []);

  return { videos, shorts, creators, loaded };
}

export interface VideoHook {
  video: VideoItem | null;
  loaded: boolean;
}

/**
 * A single video (or short) by id, async-resolved on mount / id change.
 * Tracks which id the lookup last settled for (the same technique as
 * useUserByHandle) rather than resetting `loaded` synchronously inside the
 * effect — avoids the cascading-render set-state-in-effect the lint rule flags.
 */
export function useVideo(id: string | undefined): VideoHook {
  const [video, setVideo] = useState<VideoItem | null>(null);
  const [settledId, setSettledId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (id) {
      void videosService
        .byId(id)
        .then((v) => {
          if (alive) setVideo(v);
        })
        .finally(() => {
          if (alive) setSettledId(id);
        });
    } else {
      const t = setTimeout(() => {
        setVideo(null);
        setSettledId(null);
      }, 0);
      return () => clearTimeout(t);
    }
    return () => {
      alive = false;
    };
  }, [id]);

  return { video, loaded: !id || settledId === id };
}
